import {message} from 'antd';
import Axios, {AxiosRequestConfig} from "axios";
import * as NProgress from "nprogress";
import 'nprogress/nprogress.css';
import * as React from "react";
import {AppContext} from "../AppContext";
import dayjs from "dayjs";

const XSRF_TOKEN_REFRESHED_HEADER = 'x-xsrf-token-refreshed';

type XsrfRetryRequestConfig = AxiosRequestConfig & {
    xsrfRetryAttempted?: boolean;
};

class AxiosService {

    public static setUpInterceptors(appContext: AppContext) {
        Axios.defaults.baseURL = "/api"
        Axios.defaults.withCredentials = true;
        Axios.defaults.xsrfCookieName = 'User-XSRF-TOKEN';
        Axios.defaults.headers.common['X-Requested-With'] = "XMLHttpRequest";

        if (appContext.publicAccess) {
            Axios.defaults.headers.common['X-Public-Access-User-Id'] = appContext.publicAccessUserId;
        }

        NProgress.configure({showSpinner: false});

        Axios.interceptors.request.use(config => {
            const headers = config.headers || {};

            // @ts-ignore
            config.headers = Object.assign(headers, {
                common: {
                    'Accept-Language': appContext.language,
                    'X-Timezone-Offset': dayjs().utcOffset()
                }
            });

            NProgress.start();

            return config
        });

        Axios.interceptors.response.use(response => {
                NProgress.done();

                if (response.headers['x-sal-jwt-token-expires-at']) {
                    appContext.jwtTokenExpiresAt = new Date(response.headers['x-sal-jwt-token-expires-at']);
                }

                return response
            }, error => {
                NProgress.done();

                const requestConfig = error.config as XsrfRetryRequestConfig | undefined;

                const xsrfTokenRefreshed = error.response?.status === 403 && error.response.headers[XSRF_TOKEN_REFRESHED_HEADER] === 'true';

                if (xsrfTokenRefreshed && requestConfig && !requestConfig.xsrfRetryAttempted) {
                    requestConfig.xsrfRetryAttempted = true;

                    return Axios.request(requestConfig);
                }

                if (error.response) {
                    const ignoreInterceptorHeader = error.response.headers['x-ignore-response-interceptor'];

                    if (ignoreInterceptorHeader && parseInt(ignoreInterceptorHeader, 10) === error.response.status) {
                        // handled in components
                    } else if (error.response.status === 200) {
                        // handled in components
                    } else if (error.response.status === 201) {
                        // handled in components
                    } else if (error.response.status === 202) {
                        // handled in components
                    } else if (error.response.status === 400) {
                        // handled in components
                    } else if (error.response.status === 401) {
                        appContext.user = undefined;
                    } else if (error.response.status === 403) {
                        message.error("Action is forbidden", 5);
                    } else if (error.response.status === 404) {
                        // handled in components
                    } else if (error.response.status === 409) {
                        const messageProducer = (key: string) => {
                            switch (key) {
                                case "attribute_extraction_definition_vul_source":
                                    return <>Object cannot be deleted because it's used in section <b>Definitions - Attributes</b></>;
                                case "ai_extraction_result":
                                    return <>AI extractor cannot be deleted because it is already used by saved AI extraction results. Delete those results first.</>;
                                case "ai_prompt_in_use":
                                    return <>AI prompt cannot be deleted because it is used by an extractor, a summarizer, or global AI settings.</>;
                                case "ai_report_summarizer_in_use":
                                    return <>AI report summarizer cannot be deleted because it is used by a report definition or global AI settings.</>;
                                case "ai_extractor_ai_attribute":
                                    return <>AI attribute cannot be deleted because it is assigned to an AI extractor. Remove it from the AI extractor first.</>;
                                case "ai_model_pricing_duplicate":
                                    return <>AI model pricing for this type, model, and service tier already exists.</>;
                                case "external_search_pricing_duplicate":
                                    return <>External search pricing for this backend already exists.</>;
                                default:
                                    return <>Conflict: operation cannot be completed</>;
                            }
                        }

                        message.error(messageProducer(error.response.data), 5);
                    } else if (error.response.status === 460) {
                        message.error("Monthly AI credit limit has been exceeded. AI extraction is temporarily unavailable.", 5);
                    } else if (error.response.status === 500) {
                        message.error("Server error: " + JSON.stringify(error.response.data), 5);
                    } else if (error.response.status === 520) {
                        message.error("This action is allowed only with valid license", 5);
                    } else {
                        message.error("Unexpected response from server", 5);
                    }
                }

                return Promise.reject(error);
            }
        );
    }
}

export default AxiosService;
